import { motion } from "framer-motion";
import { Mail, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

export const INQUIRY_EMAIL = "info@luxsolutions.ch";
export const INQUIRY_SUBJECT = "Anfrage Lichtplanung / Projekt – via LUX Academy";
export const LUX_SOLUTIONS_LOGO = "/lux-solutions-logo.png?v=2";
export const LUX_SOLUTIONS_MARK = "/lux-mark.png?v=2";

export function LuxSolutionsLogo({ className = "", imgClassName = "h-10 w-auto" }) {
  return (
    <img
      src={LUX_SOLUTIONS_LOGO}
      alt="Lux Solutions GmbH"
      className={`${imgClassName} ${className}`}
    />
  );
}

export function LuxSolutionsMark({ className = "", imgClassName = "h-7 w-auto rounded-md" }) {
  return (
    <img
      src={LUX_SOLUTIONS_MARK}
      alt="Lux Solutions GmbH"
      className={`${imgClassName} ${className}`}
    />
  );
}

const SOURCE_LABELS = {
  landing: "Landing-Page",
  dashboard: "Dashboard",
  briefing: "Lichtbriefing-Generator",
  ampel: "Normen-Ampel",
};

export function buildInquiryMailto({ subject = INQUIRY_SUBJECT, body = "" } = {}) {
  const parts = [`subject=${encodeURIComponent(subject)}`];
  if (body) parts.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${INQUIRY_EMAIL}?${parts.join("&")}`;
}

function trackInquiry(source) {
  try {
    fetch(`${API}/inquiry/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: source || "unbekannt" }),
      keepalive: true,
    }).catch((e) => console.error("Inquiry-Tracking fehlgeschlagen:", e));
  } catch (e) {
    console.error("Inquiry-Tracking fehlgeschlagen:", e);
  }
}

export function ProjectInquiryButton({
  subject,
  body,
  source = "unbekannt",
  label = "Lichtplanung anfragen",
  testid = "project-inquiry-button",
  variant = "primary",
  className = "",
}) {
  const sourceLine = `(Anfrage über: ${SOURCE_LABELS[source] || source})`;
  const fullBody = body ? `${body}\n\n${sourceLine}` : sourceLine;
  const styles =
    variant === "ghost"
      ? "glass hover:glow-gold text-foreground"
      : "bg-gold text-[#0a0d12] hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)]";
  return (
    <a
      href={buildInquiryMailto({ subject, body: fullBody })}
      onClick={() => trackInquiry(source)}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${styles} ${className}`}
    >
      <Mail className={`h-4 w-4 ${variant === "ghost" ? "text-gold" : ""}`} /> {label}
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

export function ProjectInquiryCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid="project-inquiry-card"
      className="relative overflow-hidden rounded-3xl glass-strong p-6 sm:p-9 mt-10"
    >
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between relative">
        <div className="max-w-2xl">
          <LuxSolutionsLogo className="mb-4" />
          <h3 className="font-heading font-bold text-2xl">Vom Wissen zum Projekt</h3>
          <p className="text-foreground/70 mt-2">
            Sie planen ein konkretes Projekt? Lassen Sie Ihre Lichtplanung von <span className="text-foreground/90 font-medium">Lux Solutions GmbH</span> umsetzen –
            vom Konzept über die normgerechte Berechnung bis zur Ausschreibung.
          </p>
        </div>
        <ProjectInquiryButton
          source="dashboard"
          testid="project-inquiry-dashboard-button"
          label="Projekt anfragen"
          body={"Guten Tag\n\nIch möchte über die LUX Architect Lighting Academy eine Lichtplanung / Projektanfrage bei Lux Solutions GmbH stellen.\n\nKurz zu meinem Vorhaben:\n- Projekt/Raum: \n- Nutzung: \n- Zeitrahmen: \n\nFreundliche Grüsse"}
          className="flex-shrink-0"
        />
      </div>
    </motion.div>
  );
}
